import { useQuery } from "@tanstack/react-query";
import type {
  ProjectDetail,
  ScriptDoc,
  ScriptRow,
  ScriptView as ScriptViewData,
  StoryboardView,
} from "@dramapie/shared";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, getProject, getScript } from "../../lib/api";
import { formatDuration } from "../../lib/format";
import {
  findScriptRow,
  groupScriptRows,
  sceneNameOf,
  scriptCounts,
  scriptSceneOf,
  siblingRows,
} from "../../lib/world";
import { ErrorState, LoadingState } from "../states";

/**
 * 剧本 / 分镜稿（R-01 ~ R-04）。
 *
 * 数据是后端 M7 `ScriptView.script`：
 * - 剧本正文（R-02）：拍 + 场次 + 动作 / 台词，**不编号镜头**；
 * - 分镜稿（R-03）：`rows[]` 逐镜行，按 `sceneId` 分组；
 * - 点行 → 右侧详情（R-04）+「在故事板看这一场」。
 */
export interface ScriptViewProps {
  projectId?: string;
  project?: ProjectDetail | null;
  /** 直接给数据（测试 / 父级已取数）；不给就自己按 projectId 取。 */
  script?: ScriptViewData | null;
  storyboard?: StoryboardView | null;
  /** 「在故事板看这一场」：带上场次与分镜稿镜号。 */
  onOpenStoryboard?: (target: { sceneId: string; rowNo: string }) => void;
  /** 关联产出（R-04）里的产出线；没接线时只展示。 */
  onOpenOutput?: (outputId: string) => void;
  /** 空态的「用 Agent 生成」。 */
  onGenerate?: () => void;
}

type ScriptMode = "prose" | "breakdown";

export function ScriptView({
  projectId,
  project: projectProp,
  script: scriptProp,
  storyboard = null,
  onOpenStoryboard,
  onOpenOutput,
  onGenerate,
}: ScriptViewProps) {
  const params = useParams<{ projectId: string }>();
  const pid = projectId ?? params.projectId ?? "";

  const projectQuery = useQuery({
    queryKey: ["project", pid],
    queryFn: () => getProject(pid),
    enabled: projectProp === undefined && pid.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const project = projectProp ?? projectQuery.data ?? null;

  const scriptQuery = useQuery({
    queryKey: ["script", pid],
    queryFn: () => getScript(pid),
    enabled: scriptProp === undefined && pid.length > 0,
    staleTime: 30_000,
    retry: false,
  });

  const view = scriptProp !== undefined ? scriptProp : (scriptQuery.data ?? null);
  const doc: ScriptDoc | null = view?.script ?? null;
  const counts = scriptCounts(doc);

  const [mode, setMode] = useState<ScriptMode>("prose");
  const [sceneId, setSceneId] = useState<string | null>(null);
  const [rowNo, setRowNo] = useState<string | null>(null);

  const groups = useMemo(() => groupScriptRows(doc), [doc]);
  const filteredGroups = useMemo(
    () => (sceneId === null ? groups : groups.filter((group) => group.scene.id === sceneId)),
    [groups, sceneId],
  );
  const visibleRows = useMemo(
    () => (doc ? (sceneId === null ? doc.rows : doc.rows.filter((row) => row.sceneId === sceneId)) : []),
    [doc, sceneId],
  );

  // 切到分镜稿 / 换筛选：保证右侧详情有一行可看。
  useEffect(() => {
    if (mode !== "breakdown") return;
    setRowNo((current) => {
      if (current && visibleRows.some((row) => row.no === current)) return current;
      return visibleRows[0]?.no ?? null;
    });
  }, [mode, visibleRows]);

  const detailRow: ScriptRow | null =
    findScriptRow(doc, rowNo) ?? visibleRows[0] ?? doc?.rows[0] ?? null;
  const detailScene = doc ? scriptSceneOf(doc, detailRow?.sceneId ?? null) : null;
  const siblings = siblingRows(doc, detailRow);
  const episode = storyboard?.episode ?? project?.episodes[0] ?? null;

  const loading = scriptProp === undefined && scriptQuery.isPending && pid.length > 0;
  const missing =
    scriptQuery.isError &&
    (scriptQuery.error instanceof ApiError ? scriptQuery.error.status === 404 : false);
  const isBlank = doc === null || (counts.rows === 0 && counts.beats === 0 && counts.scenes === 0);

  if (loading) {
    return (
      <div className="dp-script-layout">
        <div className="dp-doc-loading">
          <LoadingState label="正在读取剧本…" />
        </div>
      </div>
    );
  }

  if (scriptQuery.isError && !missing) {
    return (
      <div className="dp-script-layout">
        <div className="dp-doc-loading">
          <ErrorState
            title="剧本加载失败"
            message="读取 script/script.json 的投影失败，请重试。"
            onRetry={() => void scriptQuery.refetch()}
          />
        </div>
      </div>
    );
  }

  if (isBlank) {
    return (
      <div className="dp-doc-empty">
        <p className="dp-doc-empty-title">还没有剧本</p>
        <p className="dp-doc-empty-text">
          剧本来自 <code>script/script.json</code>：拍、场次、分镜稿行。空白骨架可以直接编辑。
        </p>
        {onGenerate ? (
          <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
            用 Agent 生成
          </button>
        ) : null}
      </div>
    );
  }

  const prose = mode === "prose";

  return (
    <div className={`dp-script-layout dp-script-view ${prose ? "is-prose" : ""}`}>
      <aside className="dp-doc-side dp-script-nav" aria-label="剧本导航">
        <div className="dp-doc-side-head">
          <span>{prose ? "剧本" : "分镜稿"}</span>
          <span>{prose ? `${counts.beats} 拍 · ${counts.scenes} 场` : `${counts.rows} 行`}</span>
        </div>

        {prose ? (
          <>
            <div className="dp-script-beats">
              {(doc?.beats ?? []).map((beat) => (
                <div key={beat.no} className="dp-script-beat">
                  <i>{beat.no}</i>
                  <span>{beat.text}</span>
                </div>
              ))}
            </div>
            <div className="dp-doc-side-head dp-script-nav-head">场次</div>
            {(doc?.scenes ?? []).map((scene) => (
              <div key={scene.id} className="dp-script-beat">
                <i>{scene.time}</i>
                <span>{scene.name}</span>
              </div>
            ))}
          </>
        ) : (
          <>
            <button
              type="button"
              className={`dp-script-scene-btn ${sceneId === null ? "is-active" : ""}`}
              onClick={() => setSceneId(null)}
            >
              全部场景 <span>{counts.rows}</span>
            </button>
            {(doc?.scenes ?? []).map((scene) => {
              const count = doc?.rows.filter((row) => row.sceneId === scene.id).length ?? 0;
              return (
                <button
                  key={scene.id}
                  type="button"
                  className={`dp-script-scene-btn ${sceneId === scene.id ? "is-active" : ""}`}
                  onClick={() => setSceneId(scene.id)}
                >
                  {scene.name} <span>{count}</span>
                </button>
              );
            })}
            <div className="dp-script-beats">
              <div className="dp-doc-side-head dp-script-nav-head">本集 {counts.beats} 拍</div>
              {(doc?.beats ?? []).map((beat) => (
                <div key={beat.no} className="dp-script-beat">
                  <i>{beat.no}</i>
                  <span>{beat.text}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>

      <main className="dp-script-main">
        <header className="dp-doc-head">
          <div className="dp-ep-eyebrow">
            {prose ? "剧本 · 文本层" : "分镜稿 · 逐镜拆解"} · 第{episode?.no ?? "—"}话
          </div>
          <h1 className="dp-doc-title">
            {prose ? "剧本" : "分镜稿"} · {episode?.title ?? "本集"}
          </h1>
          <p className="dp-doc-sub">
            {prose
              ? "场次、动作、台词；先读顺，再拆成分镜稿。"
              : "脚本拆成逐镜行；它的可视化视图就是故事板。"}
          </p>
          <div className="dp-script-mode-row">
            <div className="dp-seg dp-script-mode" role="tablist" aria-label="剧本层">
              <button
                type="button"
                role="tab"
                aria-selected={prose}
                className={prose ? "is-active" : ""}
                onClick={() => setMode("prose")}
              >
                剧本正文
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!prose}
                className={!prose ? "is-active" : ""}
                onClick={() => setMode("breakdown")}
              >
                分镜稿 · {counts.rows} 行
              </button>
            </div>
            <span className="dp-tiny-note">
              {prose ? "剧本没有镜头号" : "分镜稿有景别 / 时长 / 场次"}
            </span>
          </div>
          <div className="dp-doc-meta">
            <span>
              <b>{counts.beats}</b> 拍
            </span>
            <span>
              <b>{counts.scenes}</b> 场
            </span>
            <span>
              <b>{counts.rows}</b> 分镜稿行
            </span>
            {storyboard ? (
              <span>
                <b>{storyboard.stats.shotCount}</b> 镜
              </span>
            ) : null}
          </div>
        </header>

        {prose ? (
          <>
            <div className="dp-script-def">
              <b>剧本</b>
              <span>文字层：场次 / 动作 / 台词</span>
              <i>→</i>
              <b>分镜稿</b>
              <span>逐镜拆解：{counts.rows} 行</span>
              <i>→</i>
              <b>故事板</b>
              <span>可视化：首尾帧 / 状态</span>
            </div>
            <div className="dp-script-prose">
              <div className="dp-prose-source">
                第{episode?.no ?? "—"}话 · {episode?.title ?? ""} · {counts.beats} 拍 / {counts.scenes} 场
              </div>
              <div className="dp-prose-beats">
                {(doc?.beats ?? []).map((beat) => (
                  <div key={beat.no} className="dp-prose-beat">
                    <div className="dp-pb-n">{beat.no}</div>
                    <div>
                      <p>{beat.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="dp-prose-scenes">
                <div className="dp-lib-kicker">本集场次</div>
                {(doc?.scenes ?? []).map((scene) => {
                  const rows = doc?.rows.filter((row) => row.sceneId === scene.id) ?? [];
                  return (
                    <section key={scene.id} className="dp-prose-scene-block">
                      <header>
                        <b>{scene.name}</b>
                        <em>
                          {scene.time} · {rows.length} 行
                        </em>
                      </header>
                      {scene.summary ? <p className="dp-prose-scene-summary">{scene.summary}</p> : null}
                      {rows.length > 0 ? (
                        rows.map((row) => (
                          <div key={row.no} className="dp-prose-line">
                            <p className="dp-prose-action">{row.action}</p>
                            {row.dialogue ? <p className="dp-prose-dialogue">{row.dialogue}</p> : null}
                          </div>
                        ))
                      ) : (
                        <p className="dp-tiny-note">这一场还没有正文；后端补 rows 后自动显示。</p>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="dp-script-breakdown-note">
              这是<b>分镜稿</b>：{counts.rows} 行逐镜拆解，不是剧本。它的可视化是
              <button
                type="button"
                disabled={!onOpenStoryboard}
                onClick={() =>
                  onOpenStoryboard?.({
                    sceneId: detailRow?.sceneId ?? "",
                    rowNo: detailRow?.no ?? "",
                  })
                }
              >
                故事板
              </button>
              。
            </div>
            <div className="dp-script-rows">
              {filteredGroups.map((group) => (
                <section key={group.scene.id} className="dp-script-group">
                  <header className="dp-script-group-head">
                    <b>{group.scene.name}</b>
                    <em>
                      {group.scene.time} · {group.rows.length} 行
                    </em>
                  </header>
                  {group.rows.map((row) => (
                    <button
                      key={row.no}
                      type="button"
                      className={`dp-script-row ${detailRow?.no === row.no ? "is-active" : ""}`}
                      aria-current={detailRow?.no === row.no ? "true" : undefined}
                      aria-label={`分镜稿行 ${row.no}`}
                      onClick={() => setRowNo(row.no)}
                    >
                      <span className="dp-sr-no">{row.no}</span>
                      <span className="dp-sr-layout">
                        {row.layout}
                        {row.framing ? ` · ${row.framing}` : ""}
                      </span>
                      <span className="dp-sr-action">{row.action}</span>
                      <span className="dp-sr-dialogue">{row.dialogue || "—"}</span>
                    </button>
                  ))}
                  {group.rows.length === 0 ? (
                    <p className="dp-tiny-note">这一场还没有分镜稿行。</p>
                  ) : null}
                </section>
              ))}
              {filteredGroups.length === 0 ? (
                <div className="dp-lib-empty">这个场景没有分镜稿行。</div>
              ) : null}
            </div>
          </>
        )}
      </main>

      <aside className="dp-script-detail" aria-label="分镜稿行详情">
        {prose ? (
          <div className="dp-script-detail-inner">
            <div className="dp-lib-kicker">SCRIPT → BREAKDOWN → BOARD</div>
            <div className="dp-prose-chain">
              剧本 <i>→</i> 分镜稿 <i>→</i> 故事板
            </div>
            <p className="dp-prose-note">
              剧本是文字层：场次、动作、台词，不做镜头编号。分镜稿把它拆成 {counts.rows} 个镜头行，
              补景别、时长。故事板再把分镜稿变成可看的首尾帧、状态和确认。
            </p>
            <button
              type="button"
              className="dp-btn sm primary"
              onClick={() => setMode("breakdown")}
            >
              打开分镜稿（{counts.rows} 行）
            </button>
          </div>
        ) : detailRow ? (
          <div className="dp-script-detail-inner">
            <div className="dp-lib-kicker">SCRIPT ROW</div>
            <div className="dp-sd-no">
              {detailRow.no}
              <span>
                {detailRow.layout}
                {detailRow.framing ? ` · ${detailRow.framing}` : ""}
              </span>
            </div>
            <div className="dp-sd-scene">
              {sceneNameOf(doc, detailRow.sceneId)} · {detailScene?.time ?? ""}
            </div>
            <div className="dp-sd-block">
              <div className="dp-lib-kicker">画面 / 动作</div>
              <div className="dp-sd-text">{detailRow.action || "—"}</div>
            </div>
            <div className="dp-sd-block">
              <div className="dp-lib-kicker">台词 / 声音</div>
              <div className="dp-sd-text">{detailRow.dialogue || "—"}</div>
            </div>
            <div className="dp-sd-block">
              <div className="dp-lib-kicker">
                本场分镜稿 · {siblings.length} 行
              </div>
              <div className="dp-appear-grid">
                {siblings.slice(0, 12).map((row) => (
                  <button
                    key={row.no}
                    type="button"
                    className="dp-appear"
                    aria-label={`跳到分镜稿行 ${row.no}`}
                    onClick={() => setRowNo(row.no)}
                  >
                    {row.no}
                  </button>
                ))}
                {siblings.length > 12 ? <span className="dp-tiny-note">+{siblings.length - 12}</span> : null}
              </div>
            </div>
            <div className="dp-sd-block">
              <div className="dp-lib-kicker">时长 / 版式</div>
              <div className="dp-sd-text">
                {formatDuration(detailRow.duration)} · {detailRow.layout}
              </div>
            </div>
            <div className="dp-sd-block">
              <div className="dp-lib-kicker">关联产出</div>
              <div className="dp-sd-outputs">
                {(project?.outputs ?? []).map((output, index) => (
                  <button
                    key={output.id}
                    type="button"
                    className={`dp-sd-output ${index === 0 ? "is-current" : ""}`}
                    aria-label={`产出线 ${output.title}`}
                    onClick={() => onOpenOutput?.(output.id)}
                  >
                    {output.title}
                    <span>{index === 0 ? "当前" : "产出线"}</span>
                  </button>
                ))}
                {(project?.outputs ?? []).length === 0 ? (
                  <span className="dp-tiny-note">项目还没有产出线。</span>
                ) : null}
              </div>
            </div>
            {onOpenStoryboard ? (
              <button
                type="button"
                className="dp-btn sm primary dp-sd-board"
                onClick={() =>
                  onOpenStoryboard({ sceneId: detailRow.sceneId, rowNo: detailRow.no })
                }
              >
                在故事板看这一场
              </button>
            ) : null}
          </div>
        ) : (
          <div className="dp-shot-empty">选一行分镜稿。</div>
        )}
      </aside>
    </div>
  );
}
